/*
 * useScope — the read-path scope helpers the ARCH-2 prototype layers over the
 * whole app, ported onto the v2 store's existing `auth.activeScope`
 * (`global` | `employer`) model.
 *
 * The prototype carries a richer 4-kind scope (`all` | `direct` | `client` |
 * `project`) plus a company concept (`scopeCovers`/`inScope`/`recScope`/
 * `scopeRows`/`scopeEmployees`, proto ~L15018–15088). v2's store currently only
 * distinguishes `global` (≈ proto `all`, oversight) from `employer` (a single
 * company / client / vendor / project picked in the switcher). This module
 * provides:
 *
 *   1. PURE predicate helpers (`scopeCovers`/`inScope`/`recScope`/`scopeRows`)
 *      that mirror the prototype's read-path semantics, but operate on a passed
 *      `ScopeFilter` derived from `auth.activeScope` — no store/query coupling,
 *      fully unit-testable. They are *fail-open* exactly like the prototype:
 *      a record with no scope hint stays visible (the backend is the
 *      authoritative fail-closed gate).
 *
 *   2. A `useScope()` Vue composable that reads `auth.activeScope` + the
 *      resolved employer list (via `useScopeTree`, already cached app-wide for
 *      the OrgChip) and exposes:
 *        - `bannerProps`  — props for `<ScopeContextBanner>` (path + kind chip).
 *        - `scopePathLabel`— the `STATE.activeScope.label`-style leaf label.
 *        - `scopeSubtitle()`— a scope-aware page subtitle ("<scope> · <suffix>").
 *        - `opsEmptyNotice()`— the per-company "no records" empty-state copy.
 *
 * SLICE 3 wires this onto FIXTURE module pages only (HR Ops, Work Mgmt,
 * Performance, Field Ops + the People/Org fixture sub-tabs). The richer 4-kind
 * `ActiveScope` reconciliation lives in Slice 4 — this module deliberately does
 * NOT mutate `auth.ts`.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import { useAuthStore, type ActiveScope } from '@/stores/auth';
import { employerFullName, type Employer, type EmployerKind } from '@/api/services/employers';
import { useScopeTree, findTenantEmployer } from '@/composables/useScopeTree';
import { isV2Service, readApiHostEnv, type ApiService } from '@/api/apiHosts';
import type { ScopeKindVariant, ScopePathSegment } from '@/ui/ScopeContextBanner.vue';

/* ── Pure read-path model ───────────────────────────────────────────── */

/**
 * The flattened scope filter the pure helpers operate on. Mirrors the
 * prototype's `STATE.activeScope` shape but collapsed onto the v2 store's two
 * cases:
 *  - `{ kind: 'all' }`     — cross-scope oversight (proto `all`, store `global`).
 *  - `{ kind: 'company', employerId }` — a single picked employer (any kind).
 */
export type ScopeFilter =
  | { kind: 'all' }
  | {
      kind: 'company';
      employerId: string;
      /**
       * `true` when the company filter came from a Direct Workforce selection
       * (store `{ scope: 'direct' }`). Absent for a plain employer pick, so the
       * pre-Direct filter shape is byte-identical. Consumers that only switch on
       * `kind` treat Direct as the company root (plan X1 / Q5);
       * {@link scopeCovers} narrows it to the company's direct unit.
       */
      direct?: true;
    };

/**
 * A record's resolved scope coordinates. Fixture records may carry any of
 * `companyId` / `employerId` / `clientId` / `projectId`; absence means "no
 * hint" → the record is treated as in-scope (fail-open, matching the proto).
 */
export interface RecordScope {
  companyId?: string | null;
  employerId?: string | null;
  clientId?: string | null;
  projectId?: string | null;
}

/**
 * Resolve a fixture record's scope coordinates. Reads the common scope-key
 * fields the proto fixtures use; everything is optional so unknown records stay
 * visible.
 *
 * @param rec - The fixture record (or `null`/`undefined`).
 * @returns The record's resolved scope coordinates (all-null when absent).
 */
export function recScope(rec: RecordScope | null | undefined): Required<RecordScope> {
  return {
    companyId: rec?.companyId ?? null,
    employerId: rec?.employerId ?? null,
    clientId: rec?.clientId ?? null,
    projectId: rec?.projectId ?? null,
  };
}

/**
 * Translate the store's `ActiveScope` into the flattened {@link ScopeFilter}
 * the pure helpers consume. `null` (no selection) and `global` both collapse to
 * `{ kind: 'all' }`; an `employer` selection becomes a company filter.
 *
 * @param scope - The store's active scope (may be `null` before first pick).
 * @returns The flattened scope filter.
 */
export function toScopeFilter(scope: ActiveScope | null | undefined): ScopeFilter {
  if (scope && scope.scope === 'employer' && scope.employerId) {
    return { kind: 'company', employerId: scope.employerId };
  }
  if (scope && scope.scope === 'direct' && scope.employerId) {
    return { kind: 'company', employerId: scope.employerId, direct: true };
  }
  return { kind: 'all' };
}

/**
 * `true` when the given scope filter covers the record — i.e. the record should
 * be shown under that scope. Fail-open: a record with no matching scope hint is
 * always covered (the prototype is a fail-open UI; the backend is fail-closed).
 *
 * @param scope - The active scope filter.
 * @param rec   - The record under test (or `null`).
 * @returns Whether the record is in scope.
 */
export function scopeCovers(scope: ScopeFilter, rec: RecordScope | null | undefined): boolean {
  if (!scope || scope.kind === 'all') return true;
  const r = recScope(rec);
  // No company/employer hint at all → fail-open (keep visible).
  if (r.companyId == null && r.employerId == null) return true;
  const inCompany = r.companyId === scope.employerId || r.employerId === scope.employerId;
  // Direct Workforce (proto `scopeCovers` direct branch, L15470): only the
  // company's own direct unit — a record placed on a client or project is not.
  if (scope.direct) return inCompany && r.clientId == null && r.projectId == null;
  return inCompany;
}

/**
 * Predicate form of {@link scopeCovers} bound to a record — kept as a named
 * alias because the prototype calls the read-path guard `inScope`.
 *
 * @param rec   - The record under test.
 * @param scope - The active scope filter.
 * @returns Whether the record is in scope.
 */
export function inScope(rec: RecordScope | null | undefined, scope: ScopeFilter): boolean {
  return scopeCovers(scope, rec);
}

/**
 * Filter an array of fixture records down to those in the active scope. Mirrors
 * the prototype's `scopeRows`. Non-array inputs yield `[]`.
 *
 * @param arr   - The records to filter (or a non-array → `[]`).
 * @param scope - The active scope filter.
 * @returns The in-scope subset.
 */
export function scopeRows<T extends RecordScope>(
  arr: readonly T[] | null | undefined,
  scope: ScopeFilter,
): T[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter((r) => scopeCovers(scope, r));
}

/**
 * Convenience wrapper for the most common scope filter (the employee table) —
 * mirrors the prototype's `scopeEmployees()`. Identical to {@link scopeRows};
 * exported under the proto name so call-sites read the same.
 *
 * @param employees - The employee fixture records.
 * @param scope     - The active scope filter.
 * @returns The in-scope employees.
 */
export function scopeEmployees<T extends RecordScope>(
  employees: readonly T[] | null | undefined,
  scope: ScopeFilter,
): T[] {
  return scopeRows(employees, scope);
}

/* ── Banner / label derivation (depends on resolved employer list) ──── */

/** The proto chip kind for an employer kind. */
const KIND_VARIANT: Record<EmployerKind, ScopeKindVariant> = {
  COMPANY: 'blue',
  OS_CLIENT: 'amber',
  OS_VENDOR: 'amber',
  OS_PROJECT: 'amber',
  UNKNOWN: 'gray',
};

/**
 * The i18n key suffix (under `v2.scope.kind.*`) for an employer kind's chip
 * label. Project/client/vendor are amber; the tenant's own company is "Company".
 */
const KIND_LABEL_KEY: Record<EmployerKind, string> = {
  COMPANY: 'company',
  OS_CLIENT: 'client',
  OS_VENDOR: 'vendor',
  OS_PROJECT: 'project',
  UNKNOWN: 'company',
};

/** What `useScope()` resolves for the banner + subtitle helpers. */
export interface ResolvedScopeContext {
  /** Banner props for `<ScopeContextBanner v-bind="bannerProps">`. */
  bannerProps: ComputedRef<{
    path: ScopePathSegment[];
    kindLabel: string;
    kindVariant: ScopeKindVariant;
    hint: string;
  }>;
  /** The flattened scope filter for the pure helpers. */
  filter: ComputedRef<ScopeFilter>;
  /** `true` when on the cross-scope oversight view. */
  isOversight: ComputedRef<boolean>;
  /** The leaf scope label (proto `scopePathLabel()` — e.g. a company name). */
  scopePathLabel: ComputedRef<string>;
  /** Build a scope-aware page subtitle: `"<scope> · <suffix>"`. */
  scopeSubtitle: (suffix: string) => string;
  /** Build the per-company "no records" empty-state copy (proto opsEmptyNotice). */
  opsEmptyNotice: (label: string) => string;
  /**
   * `true` when the page has data to show for the active scope.
   *
   * For a still-fixture page (no {@link UseScopeOptions.liveService}) this is
   * the prototype's `hrOpsHasData()` / `perfHasData()` gate: `true` on the
   * oversight view OR with the tenant's own company selected, `false` once a
   * non-tenant employer (subsidiary / client / vendor / project) is in scope,
   * because the fixture sample data is recorded against the tenant company only.
   *
   * For a page whose `liveService` is already on the V2 platform the gate is
   * lifted entirely — that page reads real, per-company records from the
   * backend, so every company in scope has data of its own. See
   * {@link UseScopeOptions.liveService}.
   */
  hasTenantFixtureData: ComputedRef<boolean>;
}

/** Options for {@link useScope}. */
export interface UseScopeOptions {
  /**
   * The backend service this page reads its records from.
   *
   * Pass it once the page is off fixtures. When the named service is routed to
   * the V2 platform (`isV2Service` — i.e. `VITE_API_V2_URL` is configured and
   * the service is switched on) `hasTenantFixtureData` is `true` for every
   * company in scope, so the page renders its tabs and loads live data instead
   * of the prototype-era "this company has none in the sample data" empty
   * state. When the V2 host is NOT configured (e.g. production today) the
   * service still answers from the legacy fixture-backed host, so the original
   * tenant-only gate is kept, byte-for-byte.
   *
   * Omitted — the default — always keeps the fixture behaviour.
   */
  liveService?: ApiService;
}

/**
 * Read the active scope + resolved employer list and expose banner props,
 * scope-path label, and the scope-aware subtitle / empty-state copy helpers.
 *
 * Reuses `useScopeTree()` — already mounted app-wide for the OrgChip and cached
 * by TanStack Query — so this adds no extra network traffic on a fixture page.
 *
 * @param options Per-page scope options; see {@link UseScopeOptions}.
 * @returns The resolved scope context.
 */
export function useScope(options: UseScopeOptions = {}): ResolvedScopeContext {
  const auth = useAuthStore();
  const { activeScope } = storeToRefs(auth);
  const { t } = useI18n();
  const employers = resolveEmployers();

  const filter = computed<ScopeFilter>(() => toScopeFilter(activeScope.value));
  const isOversight = computed<boolean>(() => filter.value.kind === 'all');

  /** The currently-selected employer, resolved from the cached list. */
  const selectedEmployer = computed<Employer | null>(() => {
    if (filter.value.kind !== 'company') return null;
    const id = filter.value.employerId;
    return employers.value.find((e) => e.id === id) ?? null;
  });

  const tenantEmployer = computed<Employer | null>(() => findTenantEmployer(employers.value));

  const tenantName = computed<string>(() =>
    tenantEmployer.value ? employerFullName(tenantEmployer.value) : t('v2.scope.tenant-fallback'),
  );

  const isDirect = computed<boolean>(
    () => filter.value.kind === 'company' && filter.value.direct === true,
  );

  const scopePathLabel = computed<string>(() => {
    const sel = selectedEmployer.value;
    if (!sel) return t('v2.scope.all-companies');
    // Proto `scopePathLabel()` direct branch: "<company> (Direct)".
    if (isDirect.value) return t('v2.scope.direct-path-label', { company: employerFullName(sel) });
    return employerFullName(sel);
  });

  const bannerProps = computed(() => {
    const sel = selectedEmployer.value;
    // Oversight (no single employer): show ONLY the all-scopes crumb. Pinning the
    // tenant's own company name here ("Digital Contents Creator · All Companies")
    // was confusing — it read like a specific company was selected while in
    // all-scopes mode. The crumb now reads "All Scopes" (gray oversight chip),
    // consistent with the page-header ScopeContextBanner (same source).
    if (!sel) {
      return {
        path: [{ label: t('v2.scope.all-scopes'), strong: true }],
        kindLabel: t('v2.scope.kind.oversight'),
        kindVariant: 'gray' as ScopeKindVariant,
        hint: t('v2.scope.hint'),
      };
    }
    // Direct Workforce (proto `scopeContextBanner` direct branch, L16057):
    // "<tenant> ▸ <b>company</b> ▸ Direct Workforce" + blue "Direct" chip.
    if (isDirect.value) {
      return {
        path: [
          { label: tenantName.value, strong: false },
          { label: employerFullName(sel), strong: true },
          { label: t('v2.scope.direct-workforce'), strong: false },
        ],
        kindLabel: t('v2.scope.kind.direct'),
        kindVariant: 'blue' as ScopeKindVariant,
        hint: t('v2.scope.hint'),
      };
    }
    const kind = sel.kind;
    const path: ScopePathSegment[] = [{ label: tenantName.value }];
    // A project nests under its owning client/vendor when resolvable.
    if (kind === 'OS_PROJECT' && sel.parentId) {
      const parent = employers.value.find((e) => e.id === sel.parentId);
      if (parent) path.push({ label: employerFullName(parent) });
    }
    path.push({ label: employerFullName(sel), strong: true });
    return {
      path,
      kindLabel: t(`v2.scope.kind.${KIND_LABEL_KEY[kind]}`),
      kindVariant: KIND_VARIANT[kind],
      hint: t('v2.scope.hint'),
    };
  });

  function scopeSubtitle(suffix: string): string {
    return `${scopePathLabel.value} · ${suffix}`;
  }

  function opsEmptyNotice(label: string): string {
    return t('v2.scope.empty-notice', {
      label,
      company: selectedEmployer.value ? scopePathLabel.value : t('v2.scope.this-company'),
    });
  }

  const hasTenantFixtureData = computed<boolean>(() => {
    const sel = selectedEmployer.value;
    // Oversight (no single employer) → tenant-wide data is in view.
    if (!sel) return true;
    // The page's records come from a service that is live on the V2 platform:
    // every company has its own real data, so there is no fixture gate to apply.
    if (options.liveService && isV2Service(options.liveService, readApiHostEnv())) return true;
    // The tenant's own company owns the fixture sample data.
    return tenantEmployer.value !== null && sel.id === tenantEmployer.value.id;
  });

  return {
    bannerProps,
    filter,
    isOversight,
    scopePathLabel,
    scopeSubtitle,
    opsEmptyNotice,
    hasTenantFixtureData,
  };
}

/** A frozen empty employer list used as the no-query-client fallback. */
const EMPTY_EMPLOYERS: Employer[] = [];

/**
 * Resolve the cached employer list reactively via `useScopeTree`.
 *
 * `useScopeTree` calls `useQueryClient()`, which throws when no `VueQueryPlugin`
 * is installed (the case in many fixture-page component specs that mount a page
 * in isolation). Those specs don't need the employer list — the banner then
 * falls back to the scope's kind + the tenant-fallback label, and the page body
 * still renders. So we acquire the list defensively: if no query client is in
 * context, return a static empty ref instead of crashing the mount.
 *
 * @returns A reactive ref of the resolved employer list (empty when uncached).
 */
function resolveEmployers(): Ref<Employer[]> {
  try {
    return useScopeTree().employers;
  } catch {
    return ref(EMPTY_EMPLOYERS);
  }
}
